import { CheckedAddress } from '@sparkdotfi/common-universal'
import { useSearch } from '@tanstack/react-router'

import { useConnectedAddress } from '@/domain/wallet/useConnectedAddress'
import { UrlInjectedSearch } from '@/routing/router'

export function useIntegratorRefCode(): number | undefined {
  const { account } = useConnectedAddress({ throwOnNotConnected: false })
  const search = useSearch({ strict: false })

  if (import.meta.env.VITE_BASE_MINI_APP === '1') {
    return 129 // Base Mini App
  }

  const refCodeV1 = getRefCodeV1({ account, search })
  const refCodeV2 = getRefCodeV2({ search })
  const refCodeV3 = getRefCodeV3({ account, search })
  const refCodeV4 = getRefCodeV4({ search })

  return refCodeV1 ?? refCodeV2 ?? refCodeV3 ?? refCodeV4
}

function getRefCodeV1({
  account,
  search,
}: {
  account: CheckedAddress | undefined
  search: UrlInjectedSearch
}): number | undefined {
  const ref = search.ref?.toString()

  if (!ref || !account) {
    return undefined
  }

  const onChainRefCodesToValidate = [190, 194] // Benmo, Bitget

  for (const onChainRefCode of onChainRefCodesToValidate) {
    if (calculateQueryParamHashedRefCode(account, onChainRefCode).toString() === ref) {
      return onChainRefCode
    }
  }

  return undefined
}

function getRefCodeV2({ search }: { search: UrlInjectedSearch }): number | undefined {
  const refCode = search.ref_code?.toString()

  if (refCode === refCodesV2.benmo) {
    return 190 // Benmo
  }

  return undefined
}

function getRefCodeV3({
  account,
  search,
}: {
  account: CheckedAddress | undefined
  search: UrlInjectedSearch
}): number | undefined {
  const ref = search.ref?.toString().toLowerCase()
  const utmSource = search.utm_source?.toString().toLowerCase()

  if (account && ref === account?.toLowerCase() && utmSource === 'imtoken') {
    return 202 // IM Token
  }

  return undefined
}

function getRefCodeV4({ search }: { search: UrlInjectedSearch }): number | undefined {
  const refCode = search.ref_code?.toString()

  if (!refCode || refCode.length !== 16 || !refCode.startsWith('205')) {
    return undefined
  }

  const expiryTimestamp = parseInt(refCode.slice(3))
  const now = Date.now()
  const twentyFourHoursMs = 24 * 60 * 60 * 1000
  if (Number.isNaN(expiryTimestamp) || expiryTimestamp <= now || expiryTimestamp > now + twentyFourHoursMs) {
    return undefined
  }

  return 205 // Nansen
}

export function calculateQueryParamHashedRefCode(address: CheckedAddress, onChainRefCode: number): number {
  const str = address.toLowerCase() + onChainRefCode.toString()
  let h = 0
  for (let i = 0; i < str.length; i++) {
    h = (h << 5) - h + str.charCodeAt(i)
    h |= 0
  }
  return h >>> 0
}

export const refCodesV2 = {
  benmo: 'BENMOSPARK',
}
